"use client";

import * as React from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "@/components/ui/navigation-menu";
import { sessionCheck } from "@/lib/session";
import { handleLogout } from "@/lib/auth/utils";

export function SiteHeader() {
  const { setTheme } = useTheme();
  const router = useRouter();
  const [user, setUser] = React.useState<any>(null);

  React.useEffect(() => {
    const checkSession = async () => {
      try {
        // Setting the second parameter to false indicates that user login is not required.
        const session = await sessionCheck('', false);
        setUser(session.user);
      } catch (error) {
        setUser(null);
      }
    };
    checkSession();
  }, []);

  const onLogout = async () => {
    await handleLogout();
    setUser(null);
    router.push('/login');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center mx-auto justify-between px-2 md:px-2 px-4">
        <Link className="mr-4 flex items-center space-x-2 lg:mr-6" href="/">
          <span className="font-bold lg:inline-block">{process.env.NEXT_PUBLIC_APP_NAME ?? ''}</span>
        </Link>
        <div>
          <NavigationMenu>
            <NavigationMenuList>
              {user ? (
                <>
                  <NavigationMenuItem>
                    <span className="mr-4">Hi, {user.name || user.email}</span>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <Button size="sm" variant="ghost" onClick={onLogout}>
                      Logout
                    </Button>
                  </NavigationMenuItem>
                </>
              ) : (
                <>
                  <NavigationMenuItem>
                    <Button size="sm" asChild variant="ghost">
                      <Link href="/login">Sign In</Link>
                    </Button>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <Button size="sm" asChild>
                      <Link href="/register">Sign Up</Link>
                    </Button>
                  </NavigationMenuItem>
                </>
              )}
              <NavigationMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="focus-visible:ring-0 focus-visible:ring-offset-0">
                      <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                      <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                      <span className="sr-only">Toggle theme</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setTheme("light")}>Light</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("dark")}>Dark</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("system")}>System</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>
    </header>
  );
}
